import { redirect } from "@tanstack/react-router";
import type { IAuthContext, UserRole } from "@/contexts/AuthContext";

/**
 * Area-route beforeLoad guard. Unauthenticated users go to /login carrying
 * the destination href (the root gate restores it after sign-in);
 * authenticated users without an allowed role go to /home.
 *
 * The router only mounts once initial auth is resolved (see App.tsx), so
 * the context values here are never the loading placeholders.
 *
 * **This captures the href unconditionally, and that is deliberate.** It is the
 * writer of `?redirect=` that actually fires for an area route — `beforeLoad`
 * runs before the root component's effect — and it runs while the reader is
 * *signed out*, so it cannot know who they will turn out to be. Filtering here
 * throws the destination away for all four consumers at once (the sign-in
 * bounce, a role arriving on `/no-access`, the onboarding gate, and the
 * public-path restore), and only one of them wants it dropped.
 *
 * Filtering here breaks exactly that: a super admin who bookmarked
 * `/bu-admin/dashboard` stopped being restored to it and landed on
 * `/admin/dashboard` instead, because nothing captured meant `/home`, and
 * `/home` routes by *primary* role. Same for any admin whose session expired on
 * `/end-user/dashboard`, which every role is entitled to be on. The thing that
 * must not be overruled is the *walkthrough's* landing choice, so that is
 * filtered where the walkthrough is — the `/onboarding` branch of
 * `__root.tsx` — which loses nothing.
 */
export function requireRole(
  context: { auth: IAuthContext },
  // The href is the whole of what this reads: a filtered link is shared *for*
  // its filters, so the capture below has to keep them. Asking for a pathname
  // as well would let a caller believe one is consulted.
  location: { href: string },
  allowed: readonly UserRole[],
): void {
  if (!context.auth.isLoggedIn) {
    throw redirect({
      to: "/login",
      replace: true,
      // The href, not the pathname: a filtered link is shared *for* its
      // filters, so a pathname-only capture loses the whole content of it.
      search: { redirect: location.href },
    });
  }
  const { role } = context.auth;
  if (!role || !allowed.includes(role)) {
    throw redirect({ to: "/home", replace: true });
  }
}

/**
 * A page for the **unit's** admin only (ADR 0025 decision 4): the unit's houses,
 * its imports, its templates, its contracts, policies and analytics. A BU admin
 * who belongs to a house is sent to their dashboard rather than shown a page of
 * controls the rules will refuse — the screen is a convention, the rules the
 * boundary. Sits beside `requireRole` so a route states both in one place, and
 * here rather than in the route file so the forwarding scans (`movedRoutes`,
 * the rail's own test) keep reading a route's `throw redirect(` as "this
 * address only forwards", which a guarded page is not.
 */
export function requireUnitAdmin(context: { auth: IAuthContext }): void {
  if (context.auth.houseId) {
    throw redirect({ to: "/bu-admin/dashboard", replace: true });
  }
}
